<template>
    <div
        class="user-tile d-flex align-center text-decoration-none"
        :class="{
            'cursor-pointer': props.to !== undefined
        }"
        @click="onTileClick"
    >
        <template v-if="!loading">
            <template v-if="user">
                <UserAvatar
                    :name="full_name"
                    :size="computed_avatar_size"
                    :image-url="user_profile_image_url"
                />
                <div class="ml-2">
                    <div
                        class="text-body-2 font-weight-medium text-truncate"
                        :class="{ 'text-decoration-line-through': userIsDeleted(user) }"
                        style="letter-spacing: -0.2px !important"
                    >
                        {{ full_name }}
                        <span
                            v-if="
                                selfLabel === true &&
                                AUTH_STORE.user &&
                                AUTH_STORE.user.id === user.id
                            "
                            class="font-weight-regular text-grey-darken-1"
                            >(Ty)</span
                        >
                        <span
                            v-if="userIsDeleted(user)"
                            class="d-inline-block text-caption text-error px-2 rounded-lg bg-red-lighten-5 ml-1"
                        >
                            Użytkownik usunięty
                        </span>
                    </div>
                    <div
                        class="text-grey text-body-2 mt-n1 text-truncate"
                        :class="{ 'text-decoration-line-through': userIsDeleted(user) }"
                        v-if="subtitleKey || subtitleText"
                    >
                        {{ subtitleKey ? user[subtitleKey] : subtitleText }}
                    </div>
                </div>
            </template>
            <template v-else>
                <div
                    :style="{
                        width: `${computed_avatar_size}px`,
                        height: `${computed_avatar_size}px`
                    }"
                    class="rounded-circle bg-grey-lighten-2 d-flex align-center justify-center"
                    ref="avatar_placeholder"
                >
                    <v-icon
                        color="grey"
                        :size="Math.floor(computed_avatar_size / 2)"
                        >mdi-account-off-outline</v-icon
                    >
                </div>
                <div class="ml-2">
                    <div
                        class="text-body-2 font-weight-medium text-grey-darken-2"
                        style="letter-spacing: -0.2px !important"
                    >
                        Użytkownik niedostępny
                    </div>
                </div>
            </template>
        </template>
        <template v-else>
            <v-skeleton-loader
                class="rounded-circle mr-2"
                :width="computed_avatar_size"
                :height="computed_avatar_size"
            />
            <div>
                <v-skeleton-loader
                    width="90"
                    height="12"
                    style="border-radius: 6px"
                />
                <v-skeleton-loader
                    width="60"
                    height="8"
                    class="mt-1"
                    style="border-radius: 4px"
                />
            </div>
        </template>
    </div>
</template>

<script setup lang="ts" generic="T extends User | UserPublicFragment">
import { computed } from "vue";

import UserAvatar from "@/shared/components/user-avatar/UserAvatar.vue";
import { User, UserPublicFragment } from "@/shared/types/user";

import { useTypedRouter } from "@/composables/typed-router.composable";
import { useAuthStore } from "@/store/auth.store";

import { UserTileProps } from "./user-tile.types";

/*###########
### SETUP ###
###########*/
const props = withDefaults(defineProps<UserTileProps<T>>(), {
    loading: false,
    avatarSize: 40,
    selfLabel: true
});

const AUTH_STORE = useAuthStore();
const TYPED_ROUTER = useTypedRouter();

/*################
### USER PROPS ###
################*/
const full_name = computed(() => {
    if (!props.user) return "";
    return `${props.user.first_name} ${props.user.last_name ?? ""}`.trim();
});

const computed_avatar_size = computed(() => {
    if (
        (typeof props.avatarSize === "string" && !/^[1-9][0-9]*$/.test(props.avatarSize)) ||
        (typeof props.avatarSize === "number" && props.avatarSize % 1 !== 0)
    ) {
        console.warn(
            "Given `avatar-size` prop is invalid - must be an integer in form of number of string data type. Default size of avatar used instead."
        );
        return 40;
    }

    return typeof props.avatarSize === "number" ? props.avatarSize : parseInt(props.avatarSize);
});

const user_profile_image_url = computed(() => {
    if (!props.user || !props.user.profile_image) return undefined;

    if (computed_avatar_size.value <= 32) {
        return props.user.profile_image.url_xs;
    }
    if (computed_avatar_size.value <= 64) {
        return props.user.profile_image.url_sm;
    }
    if (computed_avatar_size.value <= 128) {
        return props.user.profile_image.url_md;
    }
    if (computed_avatar_size.value <= 256) {
        return props.user.profile_image.url_lg;
    }
    return props.user.profile_image.url_xl;
});

function userIsDeleted(u: T) {
    return (u as any).deleted === true;
}

/*###################
### CLICK HANDLER ###
###################*/
function onTileClick() {
    if (props.to) {
        TYPED_ROUTER.push(props.to);
    }
}
</script>
